/**
 * A fallback for the arrival reveals.
 *
 *    The site's motion is scroll-driven CSS, which is the right way to do it: it runs on
 *    the compositor, it stops when the reader stops, and it needs no script. But a browser
 *    that lacks `animation-timeline` gets none of it, and the sections then arrive with no
 *    motion at all - which is exactly the "correct but dead" page this rebuild is trying
 *    not to be. Where the feature is missing, an IntersectionObserver stands in for the
 *    reveals only. The parallax and the drift are not reproduced: decoration should
 *    degrade to nothing, not to a scroll listener.
 *
 *    Nothing here hides content. The CSS that sets the pre-reveal state is scoped to a
 *    class this file adds, so if the script never runs, every section is simply visible.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

const REVEAL_SELECTOR = [
  '.beeul-reveal',
  '.beeul-chooser__item',
  '.beeul-shop__item',
  '.beeul-learn__item',
  '.beeul-stories__item',
  '.beeul-pillars__item',
  '.beeul-trust__item',
  '.beeul-ingredients__item',
  '.beeul-outcomes__item',
  '.beeul-wall__item',
  '.beeul-timeline__entry',
  '.beeul-ba__item',
  '.beeul-stats__item',
  '.beeul-clip',
  '.beeul-support__point',
  '.beeul-hero__content > *',
  '.beeul-masthead__text > *',
].join(',');

function installRevealFallback() {
  if (REDUCED_MOTION.matches) return;
  if (CSS.supports('animation-timeline', 'view()')) return;
  if (!('IntersectionObserver' in window)) return;

  const root = document.documentElement;
  root.classList.add('beeul-js-reveal');

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    // Fires a little before the element is fully in, so the movement finishes about
    // where the reader's eye arrives rather than after it.
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  for (const element of document.querySelectorAll(REVEAL_SELECTOR)) {
    // Siblings in a grid stagger by position, capped so a long row never leaves the
    // last item visibly waiting.
    const siblings = element.parentElement ? [...element.parentElement.children] : [];
    const index = Math.min(siblings.indexOf(element), 5);
    if (index > 0) element.style.transitionDelay = `${index * 70}ms`;

    observer.observe(element);
  }

  // Anything already on screen at load - the hero, usually - should not wait for a
  // scroll that may never come.
  requestAnimationFrame(() => {
    for (const element of document.querySelectorAll(REVEAL_SELECTOR)) {
      const box = element.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) {
        element.classList.add('is-revealed');
        observer.unobserve(element);
      }
    }
  });
}

/* ----------------------------------------------------------------------- */

function init() {
  installRevealFallback();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Sections re-render on their own in the theme editor, so the wall is rebuilt when one
// of them comes back.
